import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';
import { UserPrivileges } from '../data/users-mock-data';
import { AccountService } from '../services/services.module';
import { OpenDialog } from './dialogs/dialog';
import { PortalDisclaimerComponent } from '../portal/shared/portal-disclaimer/portal-disclaimer.component';

class PrivilegeGuard {
  constructor(private router: Router, private accountService: AccountService) {}

  async checkForPrivileges(privilege): Promise<boolean> {
    try {
      await this.accountService.currentUserAsync();
      if (this.accountService.hasPrivilege(privilege)) {
        return true;
      } else {
        this.router.navigate(['/signin']);
        return false;
      }
    } catch (error) {
      this.router.navigate(['/signin']);
      return false;
    }
  }
  async checkDisclaimer(): Promise<boolean> {
    try {
      let currentUser = await this.accountService.currentUserAsync();
      return currentUser;
    } catch (error) {
      this.router.navigate(['/signin']);
      return null;
    }
  }

  async checkForPrivilegesAny(privileges): Promise<boolean> {
    try {
      await this.accountService.currentUserAsync();
      if (privileges.some((privilege) => this.accountService.hasPrivilege(privilege))) {
        return true;
      } else {
        this.router.navigate(['/signin']);
        return false;
      }
    } catch (error) {
      this.router.navigate(['/signin']);
      return false;
    }
  }

  async redirectToURL(data): Promise<boolean> {
    console.log(data['redirectTo']);
    if (data['redirectTo'] === 'landing' && data.hasOwnProperty('signUp')) {
      alert(1);
      this.router.navigate(['/' + data['redirectTo']], { queryParams: { signUp: data['signUp'] } });
    } else {
      this.router.navigate(['/' + data['redirectTo']]);
    }
    return false;
  }
}

@Injectable()
export class ProductSignUpPrivilegeGuard extends PrivilegeGuard implements CanActivate {
  constructor(router: Router, accountService: AccountService) {
    super(router, accountService);
  }

  canActivate(): Promise<boolean> {
    return this.checkForPrivileges(UserPrivileges.PRODUCT_SIGNUP);
  }
}

@Injectable()
export class CustomerOptionsPrivilegeGuard extends PrivilegeGuard implements CanActivate {
  constructor(router: Router, accountService: AccountService) {
    super(router, accountService);
  }

  canActivate(): Promise<boolean> {
    return this.checkForPrivileges(UserPrivileges.CUSTOMER_OPTIONS);
  }
}

@Injectable()
export class ReviewProductSignUpsPrivilegeGuard extends PrivilegeGuard implements CanActivate {
  constructor(router: Router, accountService: AccountService) {
    super(router, accountService);
  }

  canActivate(): Promise<boolean> {
    return this.checkForPrivileges(UserPrivileges.REVIEW_PRODUCT_SIGNUP);
  }
}

@Injectable()
export class CRMXViewOnlyPrivilegeGuard extends PrivilegeGuard implements CanActivate {
  constructor(router: Router, accountService: AccountService) {
    super(router, accountService);
  }

  canActivate(): Promise<boolean> {
    return this.checkForPrivileges(UserPrivileges.CRM_X_VIEW_ONLY);
  }
}

@Injectable()
export class CreateCRMXUserPrivilegeGuard extends PrivilegeGuard implements CanActivate {
  constructor(router: Router, accountService: AccountService) {
    super(router, accountService);
  }

  canActivate(): Promise<boolean> {
    return this.checkForPrivileges(UserPrivileges.CREATE_CRM_X_USER);
  }
}

@Injectable()
export class AdminOptionsPrivilegeGuard extends PrivilegeGuard implements CanActivate {
  constructor(router: Router, accountService: AccountService) {
    super(router, accountService);
  }

  canActivate(): Promise<boolean> {
    return this.checkForPrivileges(UserPrivileges.ADMIN_OPTIONS);
  }
}

@Injectable()
export class AnyPrivileges extends PrivilegeGuard implements CanActivate {
  constructor(router: Router, accountService: AccountService) {
    super(router, accountService);
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    // let roles = route.data['roles'].map(roleA => 'UserPrivileges.' + roleA);
    // return this.checkForPrivilegesAny([
    //   UserPrivileges.ADMIN_OPTIONS,
    //   UserPrivileges.REVIEW_PRODUCT_SIGNUP,
    //   UserPrivileges.RESEARCH_PORTAL_FULL_ACCESS
    // ]);
    return this.checkForPrivilegesAny(route.data['privileges']);
  }
}

@Injectable()
export class ResearchPortalFullAccessPrivilegeGuard extends PrivilegeGuard implements CanActivate {
  constructor(router: Router, accountService: AccountService) {
    super(router, accountService);
  }

  canActivate(): Promise<boolean> {
    return this.checkForPrivileges(UserPrivileges.RESEARCH_PORTAL_FULL_ACCESS);
  }
}
@Injectable()
export class ResearchPortalPartialAccessPrivilegeGuard extends PrivilegeGuard implements CanActivate {
  constructor(router: Router, accountService: AccountService) {
    super(router, accountService);
  }

  canActivate(): Promise<boolean> {
    return (
      this.checkForPrivileges(UserPrivileges.RESEARCH_PORTAL_PARTIAL_ACCESS) ||
      this.checkForPrivileges(UserPrivileges.RESEARCH_PORTAL_FULL_ACCESS)
    );
  }
}
@Injectable()
export class ViewPWMPrivilegeGuard extends PrivilegeGuard implements CanActivate {
  constructor(router: Router, accountService: AccountService) {
    super(router, accountService);
  }

  canActivate(): Promise<boolean> {
    return this.checkForPrivileges(UserPrivileges.VIEW_PWM_CUSTOMERS) && this.checkForPrivileges(UserPrivileges.VIEW_PWM_REPORTS);
  }
}

@Injectable()
export class ApprovePWMPrivilegeGuard extends PrivilegeGuard implements CanActivate {
  constructor(router: Router, accountService: AccountService) {
    super(router, accountService);
  }

  canActivate(): Promise<boolean> {
    return this.checkForPrivileges(UserPrivileges.APPROVE_PWM_REPORTS);
  }
}

@Injectable()
export class EditPWMPrivilegeGuard extends PrivilegeGuard implements CanActivate {
  constructor(router: Router, accountService: AccountService) {
    super(router, accountService);
  }

  canActivate(): Promise<boolean> {
    return (
      this.checkForPrivileges(UserPrivileges.CONFIGURE_PWM_REPORTS) &&
      this.checkForPrivileges(UserPrivileges.CREATE_PWM_CUSTOMER_GROUPS) &&
      this.checkForPrivileges(UserPrivileges.EMAIL_PWM_REPORT)
    );
  }
}

@Injectable()
export class PortalDisclaimerGuard extends PrivilegeGuard implements CanActivate {
  constructor(router: Router, accountService: AccountService) {
    super(router, accountService);
  }
  async canActivate(): Promise<boolean> {
    let currentUser = await this.checkDisclaimer();
    if (currentUser && !currentUser['portalDisclaimerAccepted']) {
      OpenDialog(PortalDisclaimerComponent, { close: true, accept: false });
    }

    return true;
  }
}

@Injectable()
export class redirectTo extends PrivilegeGuard implements CanActivate {
  constructor(router: Router, accountService: AccountService) {
    super(router, accountService);
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    return this.redirectToURL(route.data);
  }
}
